import React from 'react';
import SettingsCard from '../../organisms/SettingsCard';
import Input from '../../atoms/Input';
import Button from '../../atoms/Button';
import Chip from '../../atoms/Chip';
import Select from '../../atoms/Select';
import Radio from '../../atoms/Radio';
import DragHandleIcon from '../../atoms/DragHandleIcon';
import FieldLabel from '../../atoms/FieldLabel';
import { useSettingsWizard } from '../../../context/SettingsWizardContext';
import { QUICK_PRIORITIES, PRIORITY_COLORS } from '../../../data/settingsSteps';
import Stepper from '../../organisms/Stepper';
import { SETTINGS_STEPS } from '../../../data/settingsSteps';

function makePriority(name) {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name,
    color: '',
  };
}

export default function PrioritiesStep({ stepMeta, stepperProps }) {
  const { state, update } = useSettingsWizard();
  const { priorityDraft, priorities, defaultPriorityId } = state;

  const addPriority = (name) => {
    const trimmed = (name ?? priorityDraft).trim();
    if (!trimmed) return;
    if (priorities.some((p) => p.name.toLowerCase() === trimmed.toLowerCase())) {
      update({ priorityDraft: '' });
      return;
    }
    const next = [...priorities, makePriority(trimmed)];
    update({
      priorities: next,
      priorityDraft: '',
      defaultPriorityId: defaultPriorityId ?? next[0].id,
    });
  };

  const patchPriority = (id, patch) => {
    update({
      priorities: priorities.map((p) => (p.id === id ? { ...p, ...patch } : p)),
    });
  };

  const colorOptions = PRIORITY_COLORS.map((c) => ({
    value: c.value,
    label: c.label,
  }));

  return (
    <>
      <Stepper {...stepperProps} steps={SETTINGS_STEPS} />
      <SettingsCard title={stepMeta.title} description={stepMeta.description} showNext={false}>
      <div className="ca-priority-add">
        <FieldLabel>Priorities</FieldLabel>
        <div className="ca-priority-add__row">
          <Input
            value={priorityDraft}
            onChange={(e) => update({ priorityDraft: e.target.value })}
            placeholder="Add priority"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addPriority();
              }
            }}
          />
          <Button variant="outline" onClick={() => addPriority()}>
            + Add
          </Button>
        </div>
        <div className="ca-chip-row">
          {QUICK_PRIORITIES.map((p) => (
            <Chip key={p} onClick={() => addPriority(p)}>
              + {p}
            </Chip>
          ))}
        </div>
      </div>

      <div className="ca-priority-table">
        <div className="ca-priority-table__head">
          <div>Priority</div>
          <div>Color</div>
          <div>Default</div>
        </div>
        {(priorities.length ? priorities : [makePriority('')]).map((row, index) => {
          const isPlaceholder = !priorities.length;
          return (
            <div className="ca-priority-table__row" key={row.id || index}>
              <div className="ca-priority-table__name">
                <DragHandleIcon />
                <Input
                  value={isPlaceholder ? priorityDraft : row.name}
                  onChange={(e) => {
                    if (isPlaceholder) {
                      update({ priorityDraft: e.target.value });
                    } else {
                      patchPriority(row.id, { name: e.target.value });
                    }
                  }}
                  placeholder="Add priority"
                />
              </div>
              <Select
                value={row.color}
                onChange={(e) => {
                  if (!isPlaceholder) patchPriority(row.id, { color: e.target.value });
                }}
                options={colorOptions}
                placeholder="Select color"
              />
              <Radio
                name="default-priority"
                value={row.id}
                checked={!isPlaceholder && defaultPriorityId === row.id}
                onChange={() => !isPlaceholder && update({ defaultPriorityId: row.id })}
                aria-label="Default priority"
              />
            </div>
          );
        })}
      </div>
      </SettingsCard>
    </>
  );
}
